import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ERROR_MESSAGE } from "../../../constants/errorMessage";
import { PageRoutes } from "../../../constants/routes";
import { authSelectors } from "../../../ducks/auth";
import { clientActions, clientSelectors } from "../../../ducks/client";
import { groupActions, groupSelectors } from "../../../ducks/group";
import { systemActions, systemSelectors } from "../../../ducks/system";
import { UserRole, userActions } from "../../../ducks/user";
import { useNavigation } from "../../../hooks/useNavigation";
import { AppDispatch } from "../../../store";
import { formatDateTime } from "../../../utils/date";
import { validateEmail, validatePhoneNumber } from "../../../utils/validation";
import { appActions } from "./../../../ducks/app/actions";

export const useUserRegistration = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { navigateToPage } = useNavigation();

  const loggedInUser = useSelector(authSelectors.loggedInUser);
  const clients = useSelector(clientSelectors.clients);
  const groups = useSelector(groupSelectors.groups);
  const systems = useSelector(systemSelectors.systems);

  const [hasLoaded, setHasLoaded] = useState<boolean>(false);

  const canEditGroup = useMemo(() => {
    if (!loggedInUser) return false;

    return loggedInUser.role === UserRole.Admin;
  }, [loggedInUser]);

  useEffect(() => {
    dispatch(systemActions.getSystemList.started({}));
  }, []);

  useEffect(() => {
    if (!loggedInUser) return;

    dispatch(appActions.showLoading());

    dispatch(
      groupActions.fetchGroups.started({
        sort_key: "id",
        sort_order: "asc",
        onError: () => dispatch(appActions.hideLoading()),
        onSuccess: () => {
          if (loggedInUser.role === UserRole.Admin) {
            dispatch(
              clientActions.fetchClients.started({
                sort_key: "id",
                sort_order: "asc",
                onComplete: () => dispatch(appActions.hideLoading()),
                onSuccess: () => setHasLoaded(true),
              }),
            );
          } else {
            dispatch(appActions.hideLoading());
            setHasLoaded(true);
          }
        },
      }),
    );
  }, []);

  const handleCancel = useCallback(() => {
    dispatch(
      appActions.showModal({
        title: "内容は保存されません。よろしいですか？",
        buttons: [
          {
            variant: "dark",
            text: "閉じる",
            onClick: () => {
              dispatch(appActions.hideModal());
            },
          },
          {
            variant: "filled",
            text: "保存しない",
            onClick: () => {
              dispatch(appActions.hideModal());
              navigateToPage(PageRoutes.USERS);
            },
          },
        ],
      }),
    );
  }, []);

  const handleRegisterUser = useCallback(
    (
      firstName: string,
      lastName: string,
      email: string,
      phoneNumber: string,
      clientId: string,
      groupId: string,
      role: string,
      validFrom: string,
      validTo: string,
      systemPermissions: string[],
      onError: (message: string) => void,
    ) => {
      const isValidEmail = validateEmail(email);
      const isValidPhoneNumber = phoneNumber
        ? validatePhoneNumber(phoneNumber)
        : true;

      if (!isValidEmail) return onError(ERROR_MESSAGE.invalidEmail);

      if (phoneNumber && !isValidPhoneNumber) {
        return onError(ERROR_MESSAGE.invalidPhoneNumber);
      }

      dispatch(appActions.showLoading());
      dispatch(
        userActions.registerNewUser.started({
          first_name: firstName,
          last_name: lastName,
          email: email,
          phone: phoneNumber || undefined,
          // company_id: clientId,
          group_id: groupId,
          role: role,
          valid_from: formatDateTime(validFrom, false, "YYYY-MM-DDThh:mm:ssZ"),
          valid_to: formatDateTime(validTo, false, "YYYY-MM-DDThh:mm:ssZ"),
          system_permission: systemPermissions,
          onComplete: () => dispatch(appActions.hideLoading()),
          onSuccess: () => {
            dispatch(
              appActions.showNotification({
                type: "success",
                message: "ユーザーを登録しました",
              }),
            );
            navigateToPage(PageRoutes.USERS);
          },
        }),
      );
    },
    [],
  );

  return {
    hasLoaded,
    canEditGroup,
    clients,
    groups,
    systems,
    handleCancel,
    handleRegisterUser,
  };
};
